import React, { useState, useEffect } from 'react';
import { DollarSign, Search, CheckCircle, Receipt, AlertCircle } from 'lucide-react';
import axios from 'axios';

const ExamFeePayment = () => {
  const [filters, setFilters] = useState({ 
    course_id: '', 
    semester_id: '', 
    exam_id: '' 
  });
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [allSemesters, setAllSemesters] = useState([]);
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentData, setPaymentData] = useState({});
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [collegeInfo, setCollegeInfo] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  const paymentModes = ['Cash', 'Online', 'Card', 'UPI', 'Cheque', 'Demand Draft'];

  useEffect(() => {
    fetchCourses();
    fetchSemesters();
    fetchExams();
    fetchBankAccounts();
    loadCollegeInfo();
  }, []);

  const loadCollegeInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tenants/my-college/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSemesters(response.data);
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/examination/exams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExams(response.data);
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bank-register/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Extract banks array from response
      const banks = response.data?.banks || [];
      setBankAccounts(banks);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const handleCourseChange = (courseId) => {
    setFilters({ course_id: courseId, semester_id: '', exam_id: '' });
    
    if (courseId) {
      const filteredSemesters = allSemesters.filter(sem => sem.course_id === courseId);
      setSemesters(filteredSemesters);
    } else {
      setSemesters(allSemesters);
    }
    setStudents([]);
  };

  const loadStudents = async () => {
    if (!filters.course_id || !filters.semester_id || !filters.exam_id) {
      alert('Please select Course, Semester and Exam');
      return;
    }

    setLoading(true);
    try {
      // Get exam details
      const examResponse = await axios.get(
        `${API_URL}/api/examination/exams/${filters.exam_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const exam = examResponse.data;

      // Get students
      const studentsResponse = await axios.get(`${API_URL}/api/students/dropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const studentsData = Array.isArray(studentsResponse.data) ? studentsResponse.data : (studentsResponse.data?.data || []);
      const filteredStudents = studentsData.filter(
        s => s.course_id === filters.course_id && s.semester_id === filters.semester_id
      );

      // Check exam fee payment status for each student
      const studentsWithFeeStatus = await Promise.all(
        filteredStudents.map(async (student) => {
          try {
            const feeResponse = await axios.get(
              `${API_URL}/api/examination/exam-fee-status`,
              {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                  student_id: student.id,
                  exam_id: filters.exam_id
                }
              }
            );
            return {
              ...student,
              exam_fee: exam.exam_fee || 0,
              fee_paid: feeResponse.data.paid,
              payment_id: feeResponse.data.payment_id,
              payment_date: feeResponse.data.payment_date
            };
          } catch (error) {
            return {
              ...student,
              exam_fee: exam.exam_fee || 0,
              fee_paid: false
            };
          }
        })
      );

      setStudents(studentsWithFeeStatus);
      
    } catch (error) {
      console.error('Error loading students:', error);
      alert('Failed to load students: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openPaymentModal = (student) => {
    setSelectedStudent(student);
    setPaymentData({
      amount: student.exam_fee,
      payment_mode: 'Cash',
      bank_account_id: '',
      transaction_id: '',
      remarks: '',
      payment_date: new Date().toISOString().split('T')[0]
    });
    setShowPaymentModal(true);
  };

  const handlePayment = async () => {
    if (!paymentData.amount || paymentData.amount <= 0) {
      alert('Please enter valid amount');
      return;
    }

    if (!paymentData.payment_mode) {
      alert('Please select payment mode');
      return;
    }

    // Check if bank account is required for non-cash payments
    if (paymentData.payment_mode !== 'Cash' && !paymentData.bank_account_id) {
      alert('Please select a bank account for non-cash payment');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/examination/pay-exam-fee`,
        {
          student_id: selectedStudent.id,
          exam_id: filters.exam_id,
          amount: parseFloat(paymentData.amount),
          payment_mode: paymentData.payment_mode,
          bank_account_id: paymentData.bank_account_id,
          transaction_id: paymentData.transaction_id,
          remarks: paymentData.remarks,
          payment_date: paymentData.payment_date
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('✅ Exam fee payment successful!');
      setShowPaymentModal(false);
      loadStudents(); // Reload to update status
      
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('❌ Payment failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const generateReceipt = async (student) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/examination/generate-exam-fee-receipt`,
        {
          student_id: student.id,
          exam_id: filters.exam_id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Open receipt in new window with tenant-specific branding
      const receiptData = response.data;
      const collegeName = collegeInfo?.college_name || collegeInfo?.name || 'Institution';
      const collegeAddress = collegeInfo?.address || '';
      const receiptWindow = window.open('', '_blank');
      receiptWindow.document.write(`
        <html>
          <head>
            <title>Exam Fee Receipt</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .receipt { max-width: 600px; margin: 0 auto; border: 2px solid #000; padding: 20px; }
              h2 { text-align: center; margin-bottom: 5px; }
              .address { text-align: center; font-size: 12px; color: #666; margin-bottom: 20px; }
              .details { margin: 20px 0; }
              .row { display: flex; justify-content: space-between; margin: 10px 0; }
            </style>
          </head>
          <body>
            <div class="receipt">
              <h2>${collegeName} - Exam Fee Receipt</h2>
              ${collegeAddress ? `<div class="address">${collegeAddress}</div>` : ''}
              <div class="details">
                <div class="row"><strong>Receipt No:</strong> ${receiptData.receipt_no}</div>
                <div class="row"><strong>Date:</strong> ${receiptData.date}</div>
                <div class="row"><strong>Student Name:</strong> ${receiptData.student_name}</div>
                <div class="row"><strong>Admission No:</strong> ${receiptData.admission_no}</div>
                <div class="row"><strong>Exam:</strong> ${receiptData.exam_name}</div>
                <div class="row"><strong>Amount Paid:</strong> ₹${receiptData.amount}</div>
                <div class="row"><strong>Payment Mode:</strong> ${receiptData.payment_mode}</div>
              </div>
            </div>
            <script>window.print();</script>
          </body>
        </html>
      `);
      
    } catch (error) {
      console.error('Error generating receipt:', error);
      alert('Failed to generate receipt');
    }
  };

  const filteredExams = exams.filter(exam => 
    (!filters.course_id || exam.course_id === filters.course_id) &&
    (!filters.semester_id || exam.semester_id === filters.semester_id)
  );

  const filteredStudents = students.filter(student => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      student.full_name?.toLowerCase().includes(search) ||
      student.admission_number?.toLowerCase().includes(search) ||
      student.roll_number?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exam Fee Payment</h1>
          <p className="text-sm text-gray-500 mt-1">Collect exam fees from students</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.course_id} 
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              <option value="">Select Course</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Semester *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.semester_id} 
              onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
              disabled={!filters.course_id}
            >
              <option value="">{filters.course_id ? 'Select Semester' : 'Select course first'}</option>
              {semesters.map(sem => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Exam *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.exam_id} 
              onChange={(e) => setFilters({ ...filters, exam_id: e.target.value })}
              disabled={!filters.course_id || !filters.semester_id}
            >
              <option value="">{filters.semester_id ? 'Select Exam' : 'Select semester first'}</option>
              {filteredExams.map(exam => (
                <option key={exam.id} value={exam.id}>{exam.name} - ₹{exam.exam_fee || 0}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button 
              onClick={loadStudents}
              disabled={!filters.course_id || !filters.semester_id || !filters.exam_id || loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Loading...' : 'Load Students'}
            </button>
          </div>
        </div>
      </div>

      {students.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search by name or admission number..." 
              className="pl-10 pr-4 py-2 w-full border rounded-lg" 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
            />
          </div>
        </div>
      )}

      {students.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Exam Fee</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents.map((student) => (
                <tr key={student.id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {student.admission_number || student.roll_number || 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{student.full_name}</td>
                  <td className="px-4 py-3 text-center text-sm font-semibold">₹{student.exam_fee}</td>
                  <td className="px-4 py-3 text-center">
                    {student.fee_paid ? (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Paid
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {student.fee_paid ? (
                      <button
                        onClick={() => generateReceipt(student)}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                      >
                        <Receipt className="h-3 w-3" />
                        Receipt
                      </button>
                    ) : (
                      <button
                        onClick={() => openPaymentModal(student)}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                      >
                        <DollarSign className="h-3 w-3" />
                        Pay Now
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <DollarSign className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Select Course, Semester and Exam to load students for fee payment</p>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">Exam Fee Payment</h3>
              <p className="text-sm text-gray-600 mt-1">{selectedStudent.full_name}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date *</label>
                <input type="date" className="w-full px-3 py-2 border rounded-lg" value={paymentData.payment_date} onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })} max={new Date().toISOString().split('T')[0]} data-testid="exam-payment-date" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border rounded-lg"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode *</label>
                <select
                  className="w-full px-3 py-2 border rounded-lg"
                  value={paymentData.payment_mode}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_mode: e.target.value, bank_account_id: '' })}
                >
                  {paymentModes.map(mode => (
                    <option key={mode} value={mode}>{mode}</option>
                  ))}
                </select>
              </div>
              {paymentData.payment_mode !== 'Cash' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bank Account *</label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg"
                    value={paymentData.bank_account_id}
                    onChange={(e) => setPaymentData({ ...paymentData, bank_account_id: e.target.value })}
                  >
                    <option value="">Select Bank Account</option>
                    {bankAccounts.map(bank => (
                      <option key={bank.id} value={bank.id}>
                        {bank.bank_name} - {bank.account_number} {bank.branch ? `(${bank.branch})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Transaction ID (optional)</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-lg"
                  value={paymentData.transaction_id}
                  onChange={(e) => setPaymentData({ ...paymentData, transaction_id: e.target.value })}
                  placeholder="For online/card payments"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Remarks (optional)</label>
                <textarea
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                  value={paymentData.remarks}
                  onChange={(e) => setPaymentData({ ...paymentData, remarks: e.target.value })}
                  placeholder="Any additional notes"
                />
              </div>
            </div>
            <div className="p-6 border-t flex gap-2 justify-end">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handlePayment}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Process Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamFeePayment;
